import { now } from '@calendly/datetime'
import { CheckboxField, SubmitFormButton, TextField, useFormContext } from '@calendly/form'
import { I18n } from '@calendly/i18n'
import { DatePicker } from '@calendly/ui/components/date-picker'
import { CalendarIcon, TimeClockCircleIcon } from '@calendly/ui/components/icons'
import { LabelText, TitleText } from '@calendly/ui/components/typography'
import { useDeviceType } from '@calendly/ui/hooks/use-device-type'
import { useMemo, useState, useEffect, useRef } from 'react'

import { useEventType } from 'features/event_type_provider'

import {
  AmountContainer,
  AmountInput,
  AmountInputContainer,
  CurrencyCode,
  CurrencyDisplay,
  CurrencySymbol,
  DatePickerOverlay,
  Description,
  ExpirationDateButton,
  ExpirationDateContainer,
  FieldGroup,
  Footer,
  FormContainer,
  OptionsSection,
  PanelBody,
  PercentSymbol,
  SelectedDateText,
  StyledBanner,
  StyledCodeField,
  StyledForm,
  TimeClockCircleIconContainer,
  TimeSelectContainer,
  TimeSelectField,
  TypeButton,
  TypeToggleContainer,
  UsageLimitContainer,
  UsageLimitInput,
  UsageLimitText
} from './create_coupon_modal_styles'
import { buildValidationSchema, parseAndFormatDate } from './create_coupon_modal_utils'
import { SidePanel } from '../../../components/side_panel'


// Form Fields Component
const CouponFormFields = ({ currency, isEditMode, couponToEdit, apiError }) => {
  const { values, setFieldValue, setFieldError } = useFormContext()
  const eventType = useEventType()
  const { available_currencies: availableCurrencies } = eventType || { available_currencies: [] }
  const [showDatePicker, setShowDatePicker] = useState(false)
  const isMobile = useDeviceType('isPhone')
  const inputRef = useRef(null)

  // Check if coupon has started (startAt is not null)
  const isCouponStarted = isEditMode && couponToEdit && couponToEdit.startAt

  // Generate time options in 15-minute intervals from 0:00 (midnight) to 23:45 (11:45 PM)
  const timeOptions = useMemo(() => {
    const options = []
    for (let hour = 0; hour <= 23; hour += 1) {
      for (let minute = 0; minute < 60; minute += 15) {
        const time = now().set({ hour, minute, second: 0, millisecond: 0 })
        const value = time.toFormat('HH:mm')
        const label = time.toFormat('h:mma').toLowerCase()
        options.push({ value, name: label })
      }
    }
    return options
  }, [])

  const selectedCurrency = useMemo(
    () => availableCurrencies.find((curr) => curr.code === currency),
    [currency, availableCurrencies]
  )

  const handleTypeChange = (type) => {
    if (isCouponStarted) return
    setFieldValue('type', type)
    setFieldValue('amount', '')
  }

  // Set initial values when editing or clear when switching to creation mode
  useEffect(() => {
    if (isEditMode && couponToEdit) {
      // Set values for edit mode
      setFieldValue('name', couponToEdit.description || '')
      setFieldValue('code', couponToEdit.code || '')
      setFieldValue('type', couponToEdit.bookingCodeType === 'PERCENT' ? 'percentage' : 'fixed')
      setFieldValue('amount', couponToEdit.bookingCodeType === 'PERCENT' ? couponToEdit.percentOff : (couponToEdit.fixedAmountCents / 100))
      setFieldValue('firstTimeOnly', couponToEdit.maxPerInvitee === 1)
      setFieldValue('limitUsage', couponToEdit.maxRedemptions != null)
      setFieldValue('usageLimit', couponToEdit.maxRedemptions || '')
      setFieldValue('addExpiration', !!couponToEdit.endAt)
      if (couponToEdit.endAt) {
        const endDate = parseAndFormatDate(couponToEdit.endAt)
        if (endDate) {
          setFieldValue('expirationDate', endDate.toFormat('yyyy-MM-dd'))
          setFieldValue('expirationTime', endDate.toFormat('HH:mm'))
        }
      }
    } else if (!isEditMode) {
      // Clear fields when switching to creation mode
      setFieldValue('name', '')
      setFieldValue('code', '')
      setFieldValue('type', 'percentage')
      setFieldValue('amount', '')
      setFieldValue('firstTimeOnly', false)
      setFieldValue('limitUsage', false)
      setFieldValue('usageLimit', '')
      setFieldValue('addExpiration', false)
      setFieldValue('expirationDate', now().plus({ days: 30 }).toFormat('yyyy-MM-dd'))
      setFieldValue('expirationTime', '23:45')
    }
  }, [isEditMode, couponToEdit, setFieldValue])

  // Handle API errors
  useEffect(() => {
    if (apiError && apiError.field) {
      setFieldError(apiError.field, apiError.message)
    }
  }, [apiError, setFieldError])

  return (
    <>
      {isCouponStarted && (
        <StyledBanner type="system">
          {I18n.t('publisher.event_types.payment_method.coupon_redeemed_disabled_message')}
        </StyledBanner>
      )}

      <FieldGroup>
        <StyledCodeField
          autoFocus
          name="code"
          label={I18n.t('publisher.event_types.payment_method.code')}
          size="standard"
          required
          disabled={isCouponStarted}
        />
      </FieldGroup>

      <FieldGroup>
        <TextField
          name="name"
          label={I18n.t('publisher.event_types.payment_method.coupon_name')}
          size="standard"
          required
          disabled={isCouponStarted}
        />
        <Description>{I18n.t('publisher.event_types.payment_method.coupon_name_description')}</Description>
      </FieldGroup>

      <FieldGroup>
        <LabelText size="large">{I18n.t('publisher.event_types.payment_method.type')}</LabelText>
        <div>
          <TypeToggleContainer>
            <TypeButton
              type="button"
              selected={values.type === 'percentage'}
              onClick={() => handleTypeChange('percentage')}
              disabled={isCouponStarted}
            >
              {I18n.t('publisher.event_types.payment_method.percentage_off')}
            </TypeButton>
            <TypeButton
              type="button"
              selected={values.type === 'fixed'}
              onClick={() => handleTypeChange('fixed')}
              disabled={isCouponStarted}
            >
              {I18n.t('publisher.event_types.payment_method.fixed_amount_off')}
            </TypeButton>
          </TypeToggleContainer>
        </div>
        <AmountContainer>
          <AmountInputContainer ref={inputRef}>
            <AmountInput
              name="amount"
              type="number"
              min={values.type === 'percentage' ? 5 : 0.01}
              step={1}
              fixed={values.type === 'fixed'}
              required
              disabled={isCouponStarted}
              inputMode="numeric"
            />
          </AmountInputContainer>
          {values.type === 'percentage' && <PercentSymbol>%</PercentSymbol>}
          {values.type === 'fixed' && selectedCurrency && (
            <CurrencyDisplay>
              <CurrencySymbol>{selectedCurrency.symbol}</CurrencySymbol>
              <CurrencyCode>
                {selectedCurrency.code}
              </CurrencyCode>
            </CurrencyDisplay>
          )}
        </AmountContainer>
      </FieldGroup>

      <OptionsSection>
        <LabelText size="large">{I18n.t('publisher.event_types.payment_method.options')}</LabelText>
        <CheckboxField
          name="firstTimeOnly"
          label={I18n.t('publisher.event_types.payment_method.first_time_only')}
        />
        <CheckboxField
          name="limitUsage"
          label={I18n.t('publisher.event_types.payment_method.limit_usage')}
        />
        {values.limitUsage && (
          <UsageLimitContainer>
            <UsageLimitInput
              name="usageLimit"
              type="number"
              min="1"
              max="1000000"
              step="1"
            />
            <UsageLimitText>{I18n.t('publisher.event_types.payment_method.times')}</UsageLimitText>
          </UsageLimitContainer>
        )}
        <CheckboxField
          name="addExpiration"
          label={I18n.t('publisher.event_types.payment_method.add_expiration')}
        />
        {values.addExpiration && (
          <>
            <ExpirationDateContainer>
              <ExpirationDateButton
                decoration="secondary-outline"
                size="small"
                onClick={() => setShowDatePicker(!showDatePicker)}
              >
                <CalendarIcon spacingRight={8} />
                {values.expirationDate ? (
                  <SelectedDateText>
                    {parseAndFormatDate(values.expirationDate)?.toFormat('MMMM d, yyyy') || values.expirationDate}
                  </SelectedDateText>
                ) : (
                  <SelectedDateText>{I18n.t('publisher.event_types.payment_method.select_expiration_date')}</SelectedDateText>
                )}
              </ExpirationDateButton>
              {showDatePicker && (
                <DatePickerOverlay isMobile={isMobile}>
                  <DatePicker
                    date={values.expirationDate}
                    onChange={(date) => {
                      setFieldValue('expirationDate', date)
                      setShowDatePicker(false)
                    }}
                    minDate={now().toFormat('yyyy-MM-dd')}
                    pastSelection={false}
                  />
                </DatePickerOverlay>
              )}
            </ExpirationDateContainer>

            <TimeSelectContainer>
              <TimeClockCircleIconContainer>
                <TimeClockCircleIcon />
              </TimeClockCircleIconContainer>
              <TimeSelectField
                name="expirationTime"
                options={timeOptions}
                placeholder={I18n.t('publisher.event_types.payment_method.select_time')}
                size="standard"
              />
            </TimeSelectContainer>
          </>
        )}
      </OptionsSection>
    </>
  )
}

export const CouponModal = ({ isOpen, onClose, onSubmit, isLoading, isEditMode = false, couponToEdit = null, targetRef, apiError }) => {
  const { values: { currency } } = useFormContext()
  const isMobile = useDeviceType('isPhone')

  const initialValues = {
    name: '',
    code: '',
    type: 'percentage',
    amount: '',
    firstTimeOnly: false,
    limitUsage: false,
    usageLimit: '',
    addExpiration: false,
    expirationDate: now().plus({ days: 30 }).toFormat('yyyy-MM-dd'),
    expirationTime: '23:45'
  }

  const validationSchema = useMemo(() => buildValidationSchema(), [])

  const handleSubmit = async (formValues) => {
    if (onSubmit) {
      await onSubmit(formValues, currency, isEditMode, couponToEdit)
    }
  }

  if (!isOpen) return null

  return (
    <SidePanel
      targetRef={targetRef}
      onClose={onClose}
      header={(
        <TitleText level={2} as="h1" size="medium">
          {isEditMode
            ? I18n.t('publisher.event_types.payment_method.edit_coupon_code')
            : I18n.t('publisher.event_types.payment_method.create_coupon_code')}
        </TitleText>
      )}
    >
      <PanelBody>
        <StyledForm
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          validateOnChange={false}
          isMobile={isMobile}
        >
          <FormContainer>
            <CouponFormFields
              currency={currency}
              isEditMode={isEditMode}
              couponToEdit={couponToEdit}
              apiError={apiError}
            />
          </FormContainer>

          <Footer isMobile={isMobile}>
            <SubmitFormButton loading={isLoading}>
              {isEditMode ? I18n.t('publisher.event_types.payment_method.update') : I18n.t('publisher.event_types.payment_method.create')}
            </SubmitFormButton>
          </Footer>
        </StyledForm>
      </PanelBody>
    </SidePanel>
  )
}
